// Model registry — pricing in USD per 1,000,000 tokens.
// Tier 1 = economy, 5 = frontier. Maps to /chat max_tier param.
export const MODELS = {
  'gpt-4o-mini': {
    label: 'GPT-4o Mini', vendor: 'OpenAI', tier: 1,
    inputPer1M: 0.15, outputPer1M: 0.60,
    contextWindow: 128_000, strengths: ['fast', 'cheap', 'general'],
    color: '#7dd3fc',
  },
  'gpt-4o': {
    label: 'GPT-4o', vendor: 'OpenAI', tier: 3,
    inputPer1M: 2.50, outputPer1M: 10.00,
    contextWindow: 128_000, strengths: ['multimodal', 'reasoning'],
    color: '#38bdf8',
  },
  'gpt-4-turbo': {
    label: 'GPT-4 Turbo', vendor: 'OpenAI', tier: 4,
    inputPer1M: 10.00, outputPer1M: 30.00,
    contextWindow: 128_000, strengths: ['reasoning', 'code'],
    color: '#0ea5e9',
  },
  'deepseek-chat': {
    label: 'DeepSeek Chat', vendor: 'DeepSeek', tier: 1,
    inputPer1M: 0.14, outputPer1M: 0.28,
    contextWindow: 128_000, strengths: ['cheap', 'code', 'multilingual'],
    color: '#a78bfa',
  },
  'deepseek-reasoner': {
    label: 'DeepSeek R1', vendor: 'DeepSeek', tier: 2,
    inputPer1M: 0.55, outputPer1M: 2.19,
    contextWindow: 128_000, strengths: ['reasoning', 'math'],
    color: '#8b5cf6',
  },
  'claude-haiku': {
    label: 'Claude Haiku 4.5', vendor: 'Anthropic', tier: 2,
    inputPer1M: 0.80, outputPer1M: 4.00,
    contextWindow: 200_000, strengths: ['fast', 'quality'],
    color: '#fbbf24',
  },
  'claude-sonnet': {
    label: 'Claude Sonnet 4.6', vendor: 'Anthropic', tier: 3,
    inputPer1M: 3.00, outputPer1M: 15.00,
    contextWindow: 200_000, strengths: ['balanced', 'code', 'writing'],
    color: '#ff8c42',
  },
  'claude-opus': {
    label: 'Claude Opus 4.7', vendor: 'Anthropic', tier: 5,
    inputPer1M: 15.00, outputPer1M: 75.00,
    contextWindow: 200_000, strengths: ['frontier', 'reasoning'],
    color: '#ef4444',
  },
  'gemini-flash': {
    label: 'Gemini 1.5 Flash', vendor: 'Google', tier: 1,
    inputPer1M: 0.075, outputPer1M: 0.30,
    contextWindow: 1_000_000, strengths: ['huge context', 'fast'],
    color: '#34d399',
  },
  'gemini-pro': {
    label: 'Gemini 1.5 Pro', vendor: 'Google', tier: 3,
    inputPer1M: 1.25, outputPer1M: 5.00,
    contextWindow: 2_000_000, strengths: ['huge context', 'quality'],
    color: '#10b981',
  },
  'mistral-large': {
    label: 'Mistral Large', vendor: 'Mistral', tier: 3,
    inputPer1M: 2.00, outputPer1M: 6.00,
    contextWindow: 128_000, strengths: ['code', 'eu compliant'],
    color: '#fb923c',
  },
  'mistral-small': {
    label: 'Mistral Small', vendor: 'Mistral', tier: 1,
    inputPer1M: 0.20, outputPer1M: 0.60,
    contextWindow: 128_000, strengths: ['cheap', 'eu compliant'],
    color: '#fdba74',
  },
  'llama-3.1-70b': {
    label: 'Llama 3.1 70B', vendor: 'Meta · Together', tier: 2,
    inputPer1M: 0.88, outputPer1M: 0.88,
    contextWindow: 128_000, strengths: ['open', 'balanced'],
    color: '#60a5fa',
  },
  'qwen-2.5-coder': {
    label: 'Qwen 2.5 Coder', vendor: 'Alibaba', tier: 1,
    inputPer1M: 0.10, outputPer1M: 0.40,
    contextWindow: 128_000, strengths: ['code', 'cheap'],
    color: '#22d3ee',
  },
  'grok-2': {
    label: 'Grok 2', vendor: 'xAI', tier: 3,
    inputPer1M: 2.00, outputPer1M: 10.00,
    contextWindow: 128_000, strengths: ['real-time'],
    color: '#f472b6',
  },
  'perplexity-sonar': {
    label: 'Sonar Large', vendor: 'Perplexity', tier: 2,
    inputPer1M: 1.00, outputPer1M: 1.00,
    contextWindow: 127_000, strengths: ['search', 'citations'],
    color: '#c084fc',
  },
  'cohere-command-r-plus': {
    label: 'Command R+', vendor: 'Cohere', tier: 3,
    inputPer1M: 2.50, outputPer1M: 10.00,
    contextWindow: 128_000, strengths: ['rag', 'tools'],
    color: '#fb7185',
  },
};

export const MODEL_KEYS = Object.keys(MODELS);

export const TIER_LABELS = {
  1: 'Economy',
  2: 'Standard',
  3: 'Premium',
  4: 'Premium+',
  5: 'Frontier',
};

export const TIER_HINT = {
  1: 'Sub-$1 / 1M tokens · best for high-volume tasks',
  2: 'Strong general models · cheap reasoning',
  3: 'Best balance of quality & cost',
  4: 'Heavy reasoning · long-form generation',
  5: 'Frontier capability · pay for the tail',
};

export function modelOf(key) {
  return MODELS[key] || null;
}

export function modelsByTier(maxTier = 5) {
  return MODEL_KEYS.filter((k) => MODELS[k].tier <= maxTier);
}

export function vendorsByTier(maxTier = 5) {
  const v = new Set();
  for (const k of modelsByTier(maxTier)) v.add(MODELS[k].vendor);
  return [...v];
}
